"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from 'next/navigation';
import { useState } from "react";

import { Dropdown } from "@/components/common/dropdown";
import { segmentsIds } from "@/data/segments.data";
import { cn } from "@/utils/cn";

interface IMenuItem {
  href?: string;
  label: string;
  isDropdown?: boolean;
  items?: { href: string; label: string }[];
}

export const HEADER_HEIGHT = 75;

const Header = () => {
  const th = useTranslations("HeaderComponentLayout");

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const pathname = usePathname()

  const menuItems: IMenuItem[] = [
    { href: "/agents", label: th("menu.agents") },
    {
      isDropdown: true,
      label: th("menu.solutions"),
      items: segmentsIds.map((segment) => ({
        href: `/solutions/${segment}`,
        label: th(`solutions.${segment}`),
      })),
    },
    { href: "/cases", label: th("menu.cases") },
    {
      isDropdown: true,
      label: th("menu.platform"),
      items: [
        { href: "/platform", label: th("platform.nebula_os") },
        { href: "/platform/trust-center", label: th("platform.security") },
      ],
    },
    { href: "/about", label: th("menu.about") },
  ];

  const renderMenuItems = (
    menuItems: IMenuItem[],
    itemClass: string,
    isMobile: boolean = false
  ) => {
    return menuItems.map((item, index) =>
      item.isDropdown ? (
        <Dropdown
          key={index}
          label={item.label}
          items={item.items || []}
          isMobile={isMobile}
          aria-haspopup="true"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(false)}
        />
      ) : (
        <Link
          key={index}
          href={item.href || ""}
          className={`${itemClass} ${pathname.endsWith(item.href || "") ? "text-primary-500" : ""}`}
          onClick={() => setIsMenuOpen(false)}
        >
          {item.label}
        </Link>
      )
    );
  };

  const renderActionsButtons = (
    containerClass: string,
    loginClass: string,
    learnMoreClass: string
  ) => {
    return (
      <div className={containerClass}>
        <Link href="/profile-selection" className={loginClass} onClick={() => setIsMenuOpen(false)}>
          {th("login")}
        </Link>
        <Link href="/contact" className={learnMoreClass} onClick={() => setIsMenuOpen(false)}>
          {th("learn_more")}
        </Link>
      </div>
    );
  };

  const renderHamburgerLine = (additionalClass: string) => (
    <div
      className={`absolute left-0 w-full h-1 bg-black-600 transition-transform duration-300 ${additionalClass}`}
    />
  );

  return (
    <header
      className={`bg-white shadow-md transition-all duration-300 left-0 right-0 fixed z-50`}
      style={{ height: `${HEADER_HEIGHT}px` }}
    >
      <div className="flex items-center justify-between py-4 px-6 lg:px-[100px] w-full">
        <div
          role="banner"
          className="flex items-center space-x-8 flex-shrink-0"
        >
          <Link href="/" aria-label="StaryaAI Logo">
            <Image
              src="/logo/default.webp"
              alt="StaryaAI Logo"
              className="h-8 w-auto hidden lg:block"
              width={100}
              height={30}
            />
            <Image
              src="/logo/icon_dark.webp"
              alt="StaryaAI Logo"
              className="h-8 w-auto lg:hidden"
              width={100}
              height={30}
            />
          </Link>

          {/* Navegação (Desktop) */}
          <nav
            className="hidden md:flex space-x-6"
            aria-label="Main Navigation"
          >
            {renderMenuItems(
              menuItems,
              "text-black-600 hover:text-primary transition-colors whitespace-nowrap"
            )}
          </nav>
        </div>

        {/* Botões e Menu Hamburger */}
        <div className="flex items-center space-x-4 md:space-x-0">
          {renderActionsButtons(
            "hidden md:flex items-center space-x-2",
            "hidden md:flex text-black-600 hover:text-primary transition-colors px-4 text-base lg:text-[16px]",
            "flex items-center justify-center w-fit font-medium rounded-xl transition-all duration-200 lg:text-[16px] whitespace-nowrap text-primary border border-primary hover:bg-primary hover:text-white px-4 py-2 text-base"
          )}

          {/* Menu Hamburger (Mobile) */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            <div className="relative w-6 h-6">
              {renderHamburgerLine(
                isMenuOpen ? "top-0 rotate-45 translate-y-2.5" : "top-0"
              )}
              {renderHamburgerLine(
                isMenuOpen
                  ? "top-2.5 opacity-0"
                  : "top-2.5 opacity-100 transition-opacity"
              )}
              {renderHamburgerLine(
                isMenuOpen ? "bottom-0 -rotate-45 -translate-y-2.5" : "bottom-0"
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Navegação (Mobile) */}
      <div
        className={cn(
          "md:hidden flex flex-col bg-white justify-between overflow-hidden transition-all duration-500 ease-in-out absolute top-full left-0 right-0",
          {
            "max-h-screen opacity-100": isMenuOpen,
            "max-h-0 opacity-0 hidden": !isMenuOpen,
          }
        )}
      >
        <nav className="px-6 py-4 space-y-4" aria-label="Mobile Navigation">
          {renderMenuItems(
            menuItems,
            "block text-black-600 hover:text-primary transition-colors text-lg",
            true
          )}
        </nav>
        {renderActionsButtons(
          "flex flex-col px-6 mb-6 space-y-2 gap-4",
          "block w-full text-primary text-lg text-center hover:text-primary transition-colors",
          "block w-full bg-primary text-white text-center rounded-md py-2 hover:bg-purple-700 transition-colors"
        )}
      </div>
    </header>
  );
};

export default Header;
